/**
 * The Anatomy Atlas XR client.
 *
 * Phase 1 of the plan plus the slices of Phases 2, 4 and 5 that the capability
 * list asks for: the body in the room, systems and layer presets, search and
 * isolation, two-hand manipulation, side-by-side comparison, and tap to place.
 *
 * The rules under `src/rules` own every decision; this file owns only the
 * engine. Nothing here computes visibility, ranks a search or works out a
 * transform: it reads intent from the panel or the hands, asks a rule, and
 * applies the answer to the scene. That is the contract in docs/03-DESIGN.md §3
 * and it is what keeps the logic testable without a headset.
 */
import { AmbientLight, BoxGeometry, BufferAttribute, BufferGeometry, Color, DirectionalLight, DoubleSide, GridHelper, HemisphereLight, Line, LineBasicMaterial, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, Quaternion, REVISION, RingGeometry, SphereGeometry, Vector3 } from "three";
import { Grabbed, GrabSystem, LocomotionEnvironment, LocomotionSystem, SessionMode, TwoHandsGrabbable, World, createSystem, type Entity } from "@iwsdk/core";
import { createServiceProfile } from "@realitycollective/service-framework";
import { startServiceRuntime } from "@realitycollective/service-framework-iwsdk";
import { browserPorts, loadManifest, type AtlasPart, type Manifest } from "@atlas/runtime";
import { registerUIExtensions } from "@realitycollective/iwsdk-uiextensions";
import {
  SELECT_PRESS_THRESHOLD,
  SELECT_RELEASE_THRESHOLD,
  registerInteractions,
  routeHapticsToProvider,
  type InputSourceSnapshot,
  type InteractionEvent,
} from "@realitycollective/iwsdk-interactions";
import type { RayTuple } from "@realitycollective/iwsdk-interactions";
import { installEntryOverlay, type EntryOverlay } from "./entry-overlay.js";
import { AtlasWindows, type AboutReport, type AtlasUiPort, type CardInfo, type StatusReport } from "./ui/windows.js";
import { Picker } from "./systems/pick.js";
import { AtlasHitTester } from "./systems/hit-tester.js";
import { buildCatalog, groupsFor, type Catalog, type Group } from "./model.js";
import { AtlasScene, type AtlasBody } from "./content/atlas-scene.js";
import { LAYER_PRESETS, applyPreset, clearIsolation, defaultVisibility, hidePart, isolate, presetById, toggleSystem, trianglesVisible, resolveVisibility, type LayerPresetId, type VisibilityState } from "./rules/visibility.js";
import { DEFAULT_LIMITS, framedInFront, rotateY, scaleAbout, yawAbout, type BodyTransform, type Vec3 } from "./rules/two-hand-transform.js";
import { search } from "./rules/search.js";
import { DEFAULT_COMPARISON, bodyWidth, compareRow } from "./rules/compare-layouts.js";
import { initialPlacement, placementPrompt, placementReducer, showsReticle, type PlacementState } from "./rules/placement-machine.js";
import { HUMAN_ATLAS_TEXT, resolveInfo } from "./rules/info-resolver.js";
import { initRemoteLogger, isRemoteLogEnabled, remoteLogRequests, sessionId as remoteLogSessionId } from "./remote-logger.js";
import { PerfMonitor, perfLine, type PerfSample } from "./perf-monitor.js";

const params = new URLSearchParams(location.search);
const bundleBase = params.get("bundle") ?? (import.meta.env.DEV ? "/data/publish/latest" : "./atlas/latest");
const level = params.get("level") ?? "far";
/** `entry=off` skips the gate; the capture script and the smoke test use it. */
const showEntry = params.get("entry") !== "off";
/** `panel=off` hides the desk controls, so a capture is only the render. */
const showPanel = params.get("panel") !== "off";
const SOLO = "body";
/** A body's own root sits at this once it is parented under the grab anchor; see `installBodyAnchor()`. */
const IDENTITY_TRANSFORM: BodyTransform = { position: { x: 0, y: 0, z: 0 }, scale: 1, yaw: 0 };
/** Tint a hovered part takes; bright enough to read against every system colour. */
const HOVER_COLOUR = "#ffd479";
/** Tint the selected part keeps after the pointer moves on; distinct from the hover tint. */
const SELECTED_COLOUR = "#7fe0ff";

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
const statusEl = el("status");
const readoutEl = el("readout");

function say(text: string): void {
  statusEl.textContent = text;
  console.info(`[Atlas] ${text}`);
}

// --- state the panel edits, the rules interpret -----------------------------

interface AppState {
  /** Per-body layer state, keyed by body id. */
  visibility: Map<string, VisibilityState>;
  /** The shared transform of the solo body; comparison bodies get theirs from the layout. */
  transform: BodyTransform;
  comparing: boolean;
  /**
   * Which body the layer and selection controls act on while comparing: a body
   * id, or `null` for all of them at once.
   *
   * The row exists to hold three different states side by side, so it defaults
   * to a single body. Broadcasting to all three is a deliberate choice, because
   * the first press would otherwise flatten the comparison into three copies of
   * the same thing. Two ways to change it, and they set the same state: press a
   * body, or choose one in the panel.
   */
  active: string | null;
  selected: Group | null;
  placement: PlacementState;
}

let manifest: Manifest;
let catalog: Catalog;
let scene: AtlasScene;
let world: World;
/** The service runtime, hoisted so the AR entry/exit flow can reach `adapter.session`. */
let runtime: ReturnType<typeof startServiceRuntime>;
/** The interaction runtime and its IWSDK provider; see `installInteractions()`. */
let interactions: ReturnType<typeof registerInteractions>;
/** The atlas's own `HitTester`, shared by the interaction runtime and the desk `Picker`. */
let hitTester: AtlasHitTester;
/** Last measured cost of `interactions.runtime.update()`, for the desktop test to log. */
let lastRuntimeUpdateMs = 0;
const state: AppState = {
  visibility: new Map(),
  transform: { position: { x: 0, y: 0, z: -1.5 }, scale: 1, yaw: 0 },
  comparing: false,
  active: null,
  selected: null,
  placement: initialPlacement(),
};

/** The bodies the controls act on: the solo body, the chosen one, or all three. */
function targets(): string[] {
  if (!state.comparing) return [SOLO];
  return state.active ? [state.active] : DEFAULT_COMPARISON.map((c) => c.id);
}

/**
 * The body whose state the panel displays. Acting on all three still has to
 * show one body's layers in the checkboxes, and the first is the one the row
 * leads with.
 */
function focused(): string {
  if (!state.comparing) return SOLO;
  return state.active ?? DEFAULT_COMPARISON[0]!.id;
}

/** What to call a body on screen. */
function labelOf(id: string | null): string {
  if (!id) return "all three bodies";
  return DEFAULT_COMPARISON.find((c) => c.id === id)?.label ?? id;
}

/** The choices the body selector offers; empty when there is only one body. */
function bodyOptions(): { id: string | null; label: string }[] {
  if (!state.comparing) return [];
  return [...DEFAULT_COMPARISON.map((c) => ({ id: c.id as string | null, label: c.label })), { id: null, label: "All three" }];
}

/**
 * Aim the controls at one body, or at all of them with `null`. Both routes in
 * (pressing a body, choosing one in a panel) land here, so the two cannot
 * disagree about what is active.
 */
function setActive(id: string | null): void {
  if (!state.comparing || state.active === id) return;
  state.active = id;
  renderSystems();
  renderBodyTargets();
  refresh();
  say(`controls act on ${labelOf(id)}`);
}

/**
 * A body starts as the whole human, skin included (the "Full body" preset),
 * not as the atlas's own default set, which ships the skin hidden: the
 * starting view is the base human, and the layers come off from there.
 */
function visibilityOf(id: string): VisibilityState {
  let v = state.visibility.get(id);
  if (!v) {
    const full = presetById("full");
    state.visibility.set(id, (v = full ? applyPreset(catalog, defaultVisibility(catalog), full) : defaultVisibility(catalog)));
  }
  return v;
}

function setVisibility(id: string, next: VisibilityState): void {
  state.visibility.set(id, next);
  scene.setVisibility(id, next);
}

// --- the panel --------------------------------------------------------------

function renderSystems(): void {
  const host = el("systems");
  host.replaceChildren();
  // the checkboxes show the body the controls are aimed at, so ticking one
  // changes exactly what the panel is displaying
  const current = visibilityOf(focused());
  for (const system of [...catalog.systems].sort((a, b) => a.layer - b.layer)) {
    const label = document.createElement("label");
    const box = document.createElement("input");
    box.type = "checkbox";
    box.checked = current.systems.has(system.id);
    box.addEventListener("change", () => {
      for (const id of targets()) setVisibility(id, toggleSystem(visibilityOf(id), system.id, box.checked));
      refresh();
    });
    const swatch = document.createElement("span");
    swatch.className = "swatch";
    swatch.style.background = system.colour;
    const text = document.createElement("span");
    text.textContent = `${system.name} (${system.parts.length})`;
    label.append(box, swatch, text);
    host.append(label);
  }
}

function renderPresets(): void {
  const host = el("presets");
  host.replaceChildren();
  for (const preset of LAYER_PRESETS) {
    const button = document.createElement("button");
    button.textContent = preset.name;
    button.addEventListener("click", () => {
      for (const id of targets()) setVisibility(id, applyPreset(catalog, visibilityOf(id), preset));
      renderSystems();
      refresh();
    });
    host.append(button);
  }
}

/**
 * The body selector: one button per body plus "All three", pressed state on the
 * active one. Hidden entirely while there is only one body, because a control
 * that offers a single choice is noise.
 */
function renderBodyTargets(): void {
  const host = el("body-targets");
  const hint = el("target-hint");
  host.replaceChildren();
  const options = bodyOptions();
  if (options.length === 0) {
    hint.textContent = "";
    return;
  }
  for (const option of options) {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = option.label;
    button.dataset.body = option.id ?? "all";
    button.setAttribute("aria-pressed", String(state.active === option.id));
    button.addEventListener("click", () => setActive(option.id));
    host.append(button);
  }
  hint.textContent = "Layer and selection controls act on this body. Clicking a body in the scene picks it too.";
}

/**
 * The honesty-ladder text for the current selection (`rules/info-resolver.ts`
 * §9.4 rungs 1 and 4), or `null` with nothing selected. The one place both
 * interfaces reach this from: `uiPort.cardInfo` for the spatial window,
 * `renderCard()` below for the desk panel.
 */
function cardInfo(): CardInfo | null {
  return state.selected ? resolveInfo(HUMAN_ATLAS_TEXT, catalog, state.selected) : null;
}

/** The desk panel's card: the same fields the Selected window shows, painted from the same state. */
function renderCard(): void {
  const kind = el("card-kind");
  const name = el("card-name");
  const meta = el("card-meta");
  const source = el("card-source");
  const text = el("card-text");
  const cite = el("card-cite");
  const group = state.selected;
  if (!group) {
    kind.textContent = "";
    name.textContent = "Nothing selected";
    meta.textContent = "Pick a system or a structure from Browse or Find.";
    source.textContent = "";
    text.textContent = "";
    cite.textContent = "";
    return;
  }
  const first = catalog.byId.get(group.parts[0] ?? "");
  const system = first ? catalog.systemById.get(first.system) : undefined;
  const tris = group.parts.reduce((total, id) => total + (catalog.byId.get(id)?.tris ?? 0), 0);
  kind.textContent = group.kind.toUpperCase();
  name.textContent = group.name;
  meta.textContent = `${group.parts.length} part${group.parts.length === 1 ? "" : "s"} · ${tris.toLocaleString()} triangles${system ? ` · ${system.name}` : ""}`;
  source.textContent = group.kind === "concept" ? `FMA ${group.id.replace(/^FMA/, "")}` : group.id;
  const info = cardInfo();
  text.textContent = info?.text ?? "";
  cite.textContent = info?.source ?? "";
}

function renderResults(hits: ReturnType<typeof search>): void {
  const host = el("results");
  host.replaceChildren();
  for (const hit of hits) {
    const li = document.createElement("li");
    const name = document.createElement("span");
    name.textContent = hit.group.name;
    const kind = document.createElement("span");
    kind.className = "kind";
    kind.textContent = hit.count > 1 ? `${hit.group.kind} · ${hit.count}` : hit.group.kind;
    li.append(name, kind);
    li.addEventListener("click", () => select(hit.group));
    host.append(li);
  }
}

/**
 * Select this group on the bodies the controls are aimed at, and say what it
 * is. Pressing a body passes its id, which aims the controls there first, so
 * pointing at a body and pressing is itself a way of choosing one.
 *
 * By default the group is isolated: only its parts stay visible, which is
 * what choosing it from a list (search, Browse) means. `isolate: false`
 * selects without hiding anything: the part is tinted and described and the
 * rest of the body stays. That is what a press in the headset does, because
 * a body that vanishes on a pinch reads as the render having stopped;
 * isolating there is the Selected window's own Isolate button.
 */
function select(group: Group, bodyId?: string, options: { isolate?: boolean } = {}): void {
  const isolating = options.isolate ?? true;
  if (bodyId) setActive(bodyId);
  state.selected = group;
  if (isolating) for (const id of targets()) setVisibility(id, isolate(visibilityOf(id), group.parts));
  tintSelection(group, bodyId ?? state.active);
  const where = state.comparing ? ` on ${labelOf(state.active)}` : "";
  say(`${group.name}: ${group.parts.length} part${group.parts.length === 1 ? "" : "s"}${where}${isolating ? "" : " (Isolate on the Selected window shows it alone)"}`);
  console.info("[Atlas] select", { group: group.id, kind: group.kind, name: group.name, parts: group.parts.length, body: bodyId ?? state.active, isolate: isolating });
  renderCard();
  refresh();
}

function showEverything(): void {
  state.selected = null;
  clearSelectionTint();
  for (const id of targets()) setVisibility(id, clearIsolation(visibilityOf(id)));
  console.info("[Atlas] show everything", { bodies: [...targets()] });
  say("showing every part of the visible systems");
  renderCard();
  refresh();
}

// --- the bodies -------------------------------------------------------------

/**
 * Register one body on the interaction runtime, with a `press` behaviour —
 * required, because `runtime.flags()` only marks an interactable pressable
 * when one of its behaviours has `onPressStart`, and `behaviours: []` would
 * never receive a press. Also sets `pointerEvents = 'none'` on the body's
 * batched meshes (docs/03-DESIGN.md §17 row 5): IWSDK's own pointers must
 * never raycast a 2,234-instance mesh, only `AtlasHitTester` does, and only
 * for the runtime's targeting.
 *
 * One interactable per **body**, not one per part: measured with three
 * bodies compared (`test/desktop-ux.mjs` §6b, 6,702 registrations),
 * `interactions.runtime.update()` cost 4.1 ms a frame against a 0.5 ms
 * budget with one interactable per part — `tickBehaviours`/`updateGaze`
 * iterate every registered interactable every frame regardless of whether
 * it is being touched, and that is not free at this count. `AtlasHitTester`
 * still resolves the specific part (`hitRay`/`hitProximity` return the body
 * id to the runtime but cache the part in `lastHit()`), so
 * `onInteractionEvent` reads that to know which part a body-level event was
 * actually about. See the README's "Moving the body" for the fuller account.
 */
function registerBodyInteractables(bodyId: string): void {
  const body = scene.bodies.get(bodyId);
  if (!body) return;
  for (const mesh of body.atlas.meshes.values()) mesh.pointerEvents = "none";
  interactions.runtime.registerInteractable({ id: bodyId, behaviours: [{ kind: "press" }] });
}

function unregisterBodyInteractables(bodyId: string): void {
  interactions.runtime.unregisterInteractable(bodyId);
}

/**
 * Undo every body's interactable registrations and give the anchor its
 * child back (`releaseAnchor()`), then drop the bodies themselves. Always run
 * before a relayout, so a stale registration never survives one and
 * `AtlasScene.remove()` — which only knows how to detach its own direct
 * child from `scene.root` — can still find whatever the anchor was holding.
 */
function teardownScene(): void {
  releaseAnchor();
  for (const id of scene.bodies.keys()) unregisterBodyInteractables(id);
  scene.clear();
}

function layoutSolo(): void {
  teardownScene();
  const body = scene.add(SOLO, visibilityOf(SOLO), IDENTITY_TRANSFORM);
  attachSoloBody(body);
  applyTransformToAnchor(state.transform);
  registerBodyInteractables(SOLO);
}

function layoutComparison(): void {
  teardownScene();
  const slots = compareRow(catalog, DEFAULT_COMPARISON, { centre: { x: 0, y: 0, z: -2.2 }, scale: 0.9 });
  for (const slot of slots) {
    // each body starts from its own preset, which is the point of the comparison
    const preset = presetById(slot.preset as LayerPresetId);
    const visibility = preset ? applyPreset(catalog, defaultVisibility(catalog), preset) : defaultVisibility(catalog);
    state.visibility.set(slot.id, visibility);
    scene.add(slot.id, visibility, slot.transform);
    registerBodyInteractables(slot.id);
  }
}

function setComparing(on: boolean): void {
  if (state.comparing === on) return;
  state.comparing = on;
  // Aim at the first body rather than at all three. The row is built to show
  // three different layer states, and broadcasting by default would collapse it
  // on the first press; "All three" is one button away when that is what you want.
  state.active = on ? DEFAULT_COMPARISON[0]!.id : null;
  if (on) layoutComparison();
  else layoutSolo();
  renderSystems();
  renderBodyTargets();
  refresh();
  say(on ? `three bodies: full with skin, without skin, bones. Controls act on ${labelOf(state.active)}` : "one body");
}

function nudge(change: (t: BodyTransform) => BodyTransform): void {
  if (state.comparing) return; // the row owns its own layout
  applyTransformToAnchor(change(state.transform));
}

// --- manipulation: the engine's grab system drives the body ------------------
//
// A body anchor entity (`installBodyAnchor()`) carries `TwoHandsGrabbable`, so
// IWSDK's `GrabSystem` moves, turns and scales it with one or two hands or
// controllers alike — a hand pinch is routed to the grab sub-pointer's
// `squeeze` channel the same way a controller's squeeze button is, so there is
// no separate hand-tracking path to fall back to. The engine's transform is
// the one truth: `ManipulationSystem` only mirrors the anchor's `object3D`
// into `state.transform` every frame, and every app-driven write (nudge
// buttons, Reset, placement's placed pose, `framedInFront` at boot) goes the
// other way, through `applyTransformToAnchor()`. See `rules/two-hand-transform.ts`
// for why the pure rule still exists even though nothing here calls
// `twoHandTransform` any more.
class ManipulationSystem extends createSystem({}) {
  update(): void {
    if (state.comparing || !anchorHull) return; // the row owns its own layout while comparing
    state.transform = {
      position: { x: anchorHull.position.x, y: anchorHull.position.y, z: anchorHull.position.z },
      scale: anchorHull.scale.x,
      yaw: anchorHull.rotation.y,
    };
    const body = scene.bodies.get(SOLO);
    if (body) body.transform = state.transform;
  }
}

/**
 * The invisible hull that stands in for the body when the engine grabs it.
 *
 * `TwoHandsGrabbable` needs a raycastable mesh to touch, and the batched
 * meshes themselves are `pointerEvents = 'none'` (`registerBodyInteractables`)
 * so IWSDK's pointers never walk their 2,234 instances
 * (docs/03-DESIGN.md §17 row 5). This is that mesh instead: a box sized to the
 * catalogue's bounds, drawing nothing (`colorWrite`/`depthWrite` both false)
 * but still visible to a raycast. Its own position/rotation/scale is the
 * anchor's — see `applyTransformToAnchor()` — and the solo body's root is
 * reparented under it (`attachSoloBody()`) so moving the anchor moves the
 * body for free, the same way `body.root`'s own transform used to.
 */
let anchorEntity: Entity | null = null;
let anchorHull: Mesh | null = null;

function installBodyAnchor(): void {
  const [min, max] = catalog.bounds;
  const size = [0, 1, 2].map((k) => Math.max(0.05, (max[k] ?? 0) - (min[k] ?? 0)));
  const centre = [0, 1, 2].map((k) => ((max[k] ?? 0) + (min[k] ?? 0)) / 2);
  // Baked into the geometry, not the mesh's own position: the mesh's
  // position/rotation/scale are `applyTransformToAnchor`'s to write, exactly
  // as `body.root`'s used to be, so the box has to already sit where the body
  // does (feet at the origin) before that transform is ever applied.
  const geometry = new BoxGeometry(size[0] ?? 0.1, size[1] ?? 0.1, size[2] ?? 0.1);
  geometry.translate(centre[0] ?? 0, centre[1] ?? 0, centre[2] ?? 0);
  const hull = new Mesh(geometry, new MeshBasicMaterial({ colorWrite: false, depthWrite: false }));
  hull.name = "body-grab-hull";
  hull.visible = false; // nothing to grab before a solo body exists
  anchorEntity = world.createTransformEntity(hull);
  anchorEntity.addComponent(TwoHandsGrabbable, {
    translate: true,
    rotate: true,
    // yaw-only: the pure rule's comfort default (`DEFAULT_LIMITS.yawOnly`) —
    // free rotation of a life-size body is a common source of discomfort.
    rotateMin: [0, -Infinity, 0],
    rotateMax: [0, Infinity, 0],
    scale: true,
    scaleMin: [DEFAULT_LIMITS.minScale, DEFAULT_LIMITS.minScale, DEFAULT_LIMITS.minScale],
    scaleMax: [DEFAULT_LIMITS.maxScale, DEFAULT_LIMITS.maxScale, DEFAULT_LIMITS.maxScale],
  });
  anchorHull = hull;
  // Left wherever `createTransformEntity` parented it (the active level
  // root), deliberately: IWSDK's `TransformSystem` reparents an entity's
  // `object3D` back under the active level every frame if it is ever
  // manually parented under a plain (non-entity) `Object3D` instead, which
  // `scene.root` is — moving it there would be undone within one frame.
  // `AtlasHitTester` raycasts `world.scene` (`installInteractions()`), which
  // reaches the hull here exactly as it reaches `scene.root`.
}

/** Reparent the solo body under the anchor and reveal the hull. */
function attachSoloBody(body: AtlasBody): void {
  if (!anchorHull) return;
  anchorHull.add(body.root);
  anchorHull.visible = true;
}

/**
 * Give any body currently under the anchor back to `scene.root`, and hide and
 * release the anchor. Called before every relayout (`teardownScene()`) so
 * `AtlasScene.remove()` — which only knows how to detach its own direct
 * child — can still find the body where it left it, and so comparison mode
 * (whose row owns its own layout) never leaves the hull grabbable.
 */
function releaseAnchor(): void {
  if (!anchorHull) return;
  for (const child of [...anchorHull.children]) scene.root.add(child);
  if (anchorEntity) world.getSystem(GrabSystem)?.forceRelease(anchorEntity);
  anchorHull.visible = false;
}

/**
 * Write a transform the app computed (nudge, Reset, placement, the initial
 * framing) onto the anchor, and mirror it into the solo body's own
 * diagnostic `transform` field so `window.__atlasClient.bodies()` keeps
 * reporting the truth without `AtlasScene` ever touching `body.root`'s own
 * (identity) local transform again.
 */
function applyTransformToAnchor(t: BodyTransform): void {
  state.transform = t;
  if (!anchorHull) return;
  anchorHull.position.set(t.position.x, t.position.y, t.position.z);
  anchorHull.rotation.set(0, t.yaw, 0);
  anchorHull.scale.setScalar(t.scale);
  anchorHull.updateMatrixWorld(true);
  const body = scene.bodies.get(SOLO);
  if (body) body.transform = t;
}

// --- picking: parts through the interaction runtime --------------------------
//
// `registerInteractions` bridges IWSDK's own rig into
// `@realitycollective/webxr-interactions`'s engine-free `InteractionRuntime`.
// Bodies, not parts, are registered directly on the runtime (not as
// entities — there are no entities for parts or bodies here, only draw-batch
// slots and the anchor), so `AtlasHitTester` (`systems/hit-tester.ts`) has to
// stand in for the adapter's own entity-based hit tester; `setHitTester`
// replaces it once and the bridge never reinstalls its own afterwards.

/** True while the body anchor is currently held by the engine's grab system. */
function isBodyGrabbed(): boolean {
  return anchorEntity?.hasComponent(Grabbed) ?? false;
}

/**
 * Per-interactor bookkeeping the runtime does not itself keep: whether a
 * press is currently down, whether it has already landed on a part this
 * press (`onInteractionEvent`'s `pressStart` sets it), and whether the body
 * was grabbed at the moment the press began.
 *
 * `grabbedAtStart` is the grab-wins rule: decided once, at the rising edge
 * `onInteractionSample` detects, and honoured through to the release —
 * `runtime.getSource(id)?.nativeGrabbing` stays false here (the anchor is
 * never registered with `IWSDKInteractions.register()`, only given
 * `TwoHandsGrabbable` directly, so nothing ever tells the provider a side is
 * natively grabbing), so the reliable signal for this client is the anchor
 * entity's own `Grabbed` tag, read straight from the engine.
 */
interface InteractorTrack {
  pressed: boolean;
  hitPart: boolean;
  grabbedAtStart: boolean;
  /**
   * The fine interactable id (`${bodyId}/${partId}`) `hitTester.lastHit()`
   * named when this press started — captured then, not re-read at release,
   * because by release the ray may have drifted off the part (or the body)
   * entirely and `lastHit()` would say so; a tap still means the part it
   * started on.
   */
  pressedInteractableId: string | null;
}
const interactorTracks = new Map<string, InteractorTrack>();
function trackFor(id: string): InteractorTrack {
  let t = interactorTracks.get(id);
  if (!t) interactorTracks.set(id, (t = { pressed: false, hitPart: false, grabbedAtStart: false, pressedInteractableId: null }));
  return t;
}

/**
 * `runtime.onEvent`: hover tints the part, and a tap (`pressStart` then
 * `pressEnd`) on a part selects it — the same selection Browse makes. A tap
 * whose source was grabbing the body when the press began never selects
 * anything (the grab wins); one still in the AR "aiming" phase confirms
 * placement instead, matching the priority the old `select` listener gave
 * placement over picking.
 *
 * `event.interactableId` is only the body id (`registerBodyInteractables`
 * registers one interactable per body, not per part — see its own comment
 * for the measured reason); `hitTester.lastHit()` is where the specific part
 * a query actually resolved to lives, reliably, because nothing else queries
 * the hit tester between the runtime resolving this source's target and
 * routing the event a listener here sees (`systems/hit-tester.ts`'s comment).
 */
function onInteractionEvent(event: InteractionEvent): void {
  const interactorId = event.interactorId ?? "";
  switch (event.type) {
    case "hoverEnter":
      setHoveredInteractable(hitTester.lastHit()?.interactableId ?? null);
      return;
    case "hoverExit":
      // Only clear if this is still what is hovered: a second interactor may
      // have taken the (single, global) hover in between.
      if (hovered?.bodyId === event.interactableId) setHoveredInteractable(null);
      return;
    case "pressStart": {
      const t = trackFor(interactorId);
      t.hitPart = true;
      t.pressedInteractableId = hitTester.lastHit()?.interactableId ?? null;
      return;
    }
    case "pressEnd": {
      const t = trackFor(interactorId);
      if (t.grabbedAtStart) return; // the grab wins: this tap is not a selection
      if (state.placement.phase === "aiming") {
        placement({ type: "confirm" });
        return;
      }
      if (t.pressedInteractableId) selectInteractable(t.pressedInteractableId);
      return;
    }
    default:
      return;
  }
}

/**
 * `runtime.onSample`: the placement tap. The runtime only emits
 * `pressStart`/`pressEnd` when a ray or a poke actually lands on a
 * registered part, so a tap into empty space — the AR "confirm" and "adjust"
 * gestures — never reaches `onInteractionEvent` at all. This watches the raw
 * `select` signal instead, with the same hysteresis the runtime itself uses,
 * and acts on a release that neither hit a part nor started while the body
 * was grabbed. It replaces the old raw WebXR `select`-event listener that
 * used to live in `wireSession()`.
 */
function onInteractionSample(sources: readonly InputSourceSnapshot[]): void {
  updatePointerVisuals(sources);
  for (const source of sources) {
    const t = trackFor(source.id);
    if (!t.pressed && source.select >= SELECT_PRESS_THRESHOLD) {
      t.pressed = true;
      t.hitPart = false;
      t.grabbedAtStart = isBodyGrabbed();
    } else if (t.pressed && source.select < SELECT_RELEASE_THRESHOLD) {
      t.pressed = false;
      if (t.hitPart || t.grabbedAtStart) continue;
      if (state.placement.phase === "aiming") placement({ type: "confirm" });
      else if (state.placement.phase === "placed") placement({ type: "adjust" });
    }
  }
}

// --- pointer rays -----------------------------------------------------------
//
// Where each hand or controller points, and the cursor where its ray lands.
// The engine draws no ray of its own for the batched body (it is not an
// interactable entity), and without one a press in the headset lands on a
// part nobody could see they were aiming at. One thin line and one small
// sphere per source, moved from the runtime's own samples every frame; the
// line ends where the hit tester answered for that same ray this frame, so
// the cursor sits on the part the hover tint names.

/** Metres of ray to draw when it lands on nothing. */
const POINTER_RAY_LENGTH = 1.2;
interface PointerVisual {
  line: Line;
  cursor: Mesh;
  positions: Float32Array;
  seen: number;
}
const pointerVisuals = new Map<string, PointerVisual>();
let pointerRoot: Object3D | null = null;
let pointerFrame = 0;
const pointerEnd = new Vector3();

function installPointerVisuals(): void {
  pointerRoot = new Object3D();
  pointerRoot.name = "pointer-rays";
  pointerRoot.visible = false;
  world.scene.add(pointerRoot);
}

function pointerVisualFor(id: string): PointerVisual | null {
  if (!pointerRoot) return null;
  const existing = pointerVisuals.get(id);
  if (existing) return existing;
  const positions = new Float32Array(6);
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new BufferAttribute(positions, 3));
  const line = new Line(geometry, new LineBasicMaterial({ color: 0x9cc9ff, transparent: true, opacity: 0.6 }));
  line.frustumCulled = false;
  line.name = `pointer-ray:${id}`;
  line.pointerEvents = "none";
  const cursor = new Mesh(new SphereGeometry(0.006, 12, 8), new MeshBasicMaterial({ color: HOVER_COLOUR }));
  cursor.frustumCulled = false;
  cursor.name = `pointer-cursor:${id}`;
  cursor.pointerEvents = "none";
  pointerRoot.add(line, cursor);
  const visual: PointerVisual = { line, cursor, positions, seen: 0 };
  pointerVisuals.set(id, visual);
  return visual;
}

/** Move every source's ray and cursor to this frame's sample; hide the ones that went away. */
function updatePointerVisuals(sources: readonly InputSourceSnapshot[]): void {
  if (!pointerRoot) return;
  const presenting = world.renderer.xr.isPresenting === true;
  pointerRoot.visible = presenting;
  if (!presenting) return;
  pointerFrame += 1;
  for (const source of sources) {
    if (!source.ray || (source.kind !== "controller" && source.kind !== "hand")) continue;
    const visual = pointerVisualFor(source.id);
    if (!visual) continue;
    visual.seen = pointerFrame;
    const answer = hitTester.lastAnswerFor(source.ray);
    const hit = typeof answer === "number";
    const length = hit ? answer : POINTER_RAY_LENGTH;
    const [ox, oy, oz] = source.ray.origin;
    const [dx, dy, dz] = source.ray.direction;
    pointerEnd.set(ox + dx * length, oy + dy * length, oz + dz * length);
    const p = visual.positions;
    p[0] = ox;
    p[1] = oy;
    p[2] = oz;
    p[3] = pointerEnd.x;
    p[4] = pointerEnd.y;
    p[5] = pointerEnd.z;
    (visual.line.geometry.getAttribute("position") as BufferAttribute).needsUpdate = true;
    visual.line.visible = true;
    visual.cursor.visible = hit;
    if (hit) {
      visual.cursor.position.copy(pointerEnd);
      // grows with distance, so it reads the same from arm's length to across the room
      visual.cursor.scale.setScalar(1 + length);
    }
  }
  for (const visual of pointerVisuals.values()) {
    if (visual.seen !== pointerFrame) {
      visual.line.visible = false;
      visual.cursor.visible = false;
    }
  }
}

const headPosition = new Vector3();
const headQuaternion = new Quaternion();
const headForward = new Vector3();
/** A ray cast from the head entity's pose: the runtime's own gaze ray, which this client does not use. */
function isHeadRay(ray: RayTuple): boolean {
  const head = world.playerSpaceEntities.head.object3D;
  if (!head) return false;
  head.getWorldPosition(headPosition);
  head.getWorldQuaternion(headQuaternion);
  headForward.set(0, 0, -1).applyQuaternion(headQuaternion);
  const e = 1e-4;
  return (
    Math.abs(headPosition.x - ray.origin[0]) < e &&
    Math.abs(headPosition.y - ray.origin[1]) < e &&
    Math.abs(headPosition.z - ray.origin[2]) < e &&
    Math.abs(headForward.x - ray.direction[0]) < e &&
    Math.abs(headForward.y - ray.direction[1]) < e &&
    Math.abs(headForward.z - ray.direction[2]) < e
  );
}

/**
 * One-time setup: the runtime, the atlas's own hit tester, haptics, and the
 * two subscriptions above. Called once `scene` exists, because
 * `AtlasHitTester` reads it.
 *
 * Also times `interactions.runtime.update()` (a monkey-patch around the one
 * public method that does the per-frame work): the design budget is 0.5 ms
 * per frame with three bodies compared, and `window.__atlasClient.runtimeUpdateMs()`
 * is how `test/desktop-ux.mjs` checks it without a profiler.
 */
function installInteractions(): void {
  interactions = registerInteractions(world, { nativeGrab: true });
  // `world.scene`, not `scene.root`: the solo body's root ends up parented
  // under the grab anchor, which IWSDK keeps under the active level root
  // rather than wherever `AtlasScene.root` sits — see `AtlasHitTester`'s and
  // `installBodyAnchor()`'s comments. Both are reachable from `world.scene`.
  hitTester = new AtlasHitTester(scene, world.scene);
  // The runtime resolves a head-gaze ray through the hit tester every frame
  // whenever the provider reports a head pose, which the IWSDK provider always
  // does, session or not, and nothing here registers a gaze interactable.
  // Against a 2,234-instance body that raycast was most of the runtime's
  // per-frame cost (docs/06-REALITY_COLLECTIVE_ASKS.md §1.5), so the head ray
  // is recognised and declined before it reaches the atlas: the provider
  // builds it from the head entity's world pose, and so does this check.
  interactions.runtime.setHitTester({
    hitRay: (ray) => (isHeadRay(ray) ? null : hitTester.hitRay(ray)),
    hitProximity: (point, radius) => hitTester.hitProximity(point, radius),
  });
  routeHapticsToProvider((listener) => interactions.runtime.onFeedback(listener), interactions.provider);
  interactions.runtime.onEvent(onInteractionEvent);
  interactions.runtime.onSample(onInteractionSample);

  const originalUpdate = interactions.runtime.update.bind(interactions.runtime);
  interactions.runtime.update = (dt: number): void => {
    const start = performance.now();
    originalUpdate(dt);
    lastRuntimeUpdateMs = performance.now() - start;
  };
}

// --- placement: the machine decides, the session reports --------------------

function placement(event: Parameters<typeof placementReducer>[1]): void {
  const before = state.placement.phase;
  state.placement = placementReducer(state.placement, event);
  if (state.placement.phase !== before) console.info("[Atlas] placement", `${before} -> ${state.placement.phase}`, event.type);
  say(placementPrompt(state.placement));
  if (state.placement.phase === "placed" && state.placement.pose) {
    const { position, yaw } = state.placement.pose;
    applyTransformToAnchor({ position, scale: state.transform.scale, yaw });
  }
}

/** What a desk offers instead of a headset, said the same way everywhere. */
const DESK_CONTROLS = "W A S D or the arrow keys to walk, Shift to hurry, drag to look, click a part to select it";

/**
 * Ask for a VR session, and say something either way.
 *
 * `launchXR` on a machine with no headset does nothing and reports nothing, so
 * pressing the button used to be indistinguishable from a broken page. There is
 * a real answer to give now: the desk build walks, so the button explains that
 * rather than apologising.
 */
async function enterVr(): Promise<void> {
  const supported = await navigator.xr?.isSessionSupported("immersive-vr").catch(() => false);
  if (!supported) {
    say(`No headset found. You can still explore from here: ${DESK_CONTROLS}.`);
    return;
  }
  try {
    world.launchXR({ sessionMode: SessionMode.ImmersiveVR });
  } catch (error) {
    say(`Could not enter VR: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/** Cached result of the one `isSessionSupported("immersive-ar")` probe; `null` while it runs. */
let arSupported: boolean | null = null;

/**
 * True from the moment `toggleAr()` decides to end a VR session until the AR
 * request has settled. `wireSession`'s `sessionend` handler resets the
 * placement machine, and the renderer's event can fire after the facet's
 * `end()` has resolved and "offer" has already moved the machine to
 * `requesting`; a reset then would drop it back to `idle`, `granted` would be
 * ignored, and the reticle would never appear. While the swap is in flight
 * the machine is the swap's to drive.
 */
let arSwapInProgress = false;

/** Whether the live session is AR, by blend mode rather than by which mode was last requested. */
function inAr(): boolean {
  if (!world) return false; // the AR probe can resolve before World.create() does
  const session = world.renderer.xr.getSession();
  return !!session && session.environmentBlendMode !== "opaque";
}

/**
 * Enter AR from 2D or VR, or leave it for the VR studio: one function for
 * both directions, shared by the DOM `#enter-ar` button and the Selected
 * window's `act-ar`, because "Place in my room" and "Back to the studio" are
 * the same decision seen from either side of it.
 *
 * The support probe runs first and its result is cached in `arSupported`: the
 * facet can only report "denied" for a request, which looks exactly like a
 * dismissed prompt, so unsupported has to come from the dedicated probe and,
 * once known, is not asked again by ending a perfectly good VR session over
 * it. Reference implementation of the same VR/AR swap: pale-signal's
 * `PassthroughSystem` (`src/systems/passthrough.ts`).
 */
async function toggleAr(): Promise<void> {
  const session = runtime.adapter.session;
  if (!session) return;

  if (world.renderer.xr.isPresenting && inAr()) {
    await session.end();
    await session.request("immersive-vr", { timeoutMs: 15_000 });
    renderArButton();
    return;
  }

  if (arSupported === null) {
    arSupported = (await navigator.xr?.isSessionSupported("immersive-ar").catch(() => false)) ?? false;
    console.info("[Atlas] AR probe", { supported: arSupported });
    renderArButton();
  }
  if (!arSupported) {
    placement({ type: "unsupported", reason: "This headset cannot place things in your room" });
    return;
  }

  // End any VR session before touching the placement machine: `wireSession`'s
  // `sessionend` handler fires `placement({type:"exit"})` unconditionally, and
  // running that after "offer" would reset "requesting" straight back to idle
  // before the AR request had even gone out.
  const cameFromVr = world.renderer.xr.isPresenting;
  arSwapInProgress = true;
  try {
    if (cameFromVr) await session.end();
    placement({ type: "offer" });
    const result = await session.request("immersive-ar", {
      timeoutMs: 15_000,
      requiredFeatures: ["hit-test"],
      optionalFeatures: ["anchors", "hand-tracking", "local-floor", "layers"],
    });
    if (!result.ok) {
      placement({ type: "denied", reason: result.reason });
      if (cameFromVr) await session.request("immersive-vr", { timeoutMs: 15_000 });
      return;
    }
    // `wireSession`'s sessionstart handler carries the machine on from here.
  } finally {
    arSwapInProgress = false;
  }
}

/** The DOM button's label; never hidden on the desk build, unlike the window's. */
function renderArButton(): void {
  el<HTMLButtonElement>("enter-ar").textContent = inAr() ? "Back to the studio" : "Place in my room";
}

/**
 * The reticle, driven by a real hit test against the room. Held here rather
 * than in the machine because it is engine state; the machine only ever sees
 * the pose it produces.
 */
let hitTestSource: XRHitTestSource | null = null;

/** The floor grid; hidden in AR, where IWSDK's EnvironmentSystem already clears the sky. */
let gridHelper: GridHelper | null = null;

/** Whether `"layers"` was granted, captured at session start; `null` with no live session. */
let layersEnabled: boolean | null = null;

/**
 * The reticle: a thin ring at the hit pose while aiming, hidden otherwise.
 * A child of `reticleRoot` rather than posed directly, because the hit pose's
 * orientation puts its local +Y along the surface normal, and `RingGeometry`
 * faces +Z; tilting the ring -90° about X inside the root turns that into
 * "lies flat against whatever the root is aligned to" without the two facts
 * (root orientation from the hit, ring tilt to lie flat) fighting each other.
 */
let reticleRoot: Object3D | null = null;

function installReticle(): void {
  reticleRoot = new Object3D();
  reticleRoot.name = "placement-reticle";
  const ring = new Mesh(
    new RingGeometry(0.09, 0.125, 32),
    new MeshBasicMaterial({ color: 0x8fd0ff, transparent: true, opacity: 0.85, side: DoubleSide, depthWrite: false }),
  );
  ring.rotation.x = -Math.PI / 2;
  reticleRoot.add(ring);
  reticleRoot.visible = false;
  world.scene.add(reticleRoot);
}

async function startHitTesting(session: XRSession): Promise<void> {
  const viewer = await session.requestReferenceSpace("viewer").catch(() => null);
  if (!viewer || typeof session.requestHitTestSource !== "function") {
    placement({ type: "unsupported", reason: "This headset cannot find surfaces" });
    return;
  }
  hitTestSource = (await session.requestHitTestSource({ space: viewer })) ?? null;
  if (hitTestSource) placement({ type: "granted" });
}

/** Sample the hit test each frame while the user is choosing a spot. */
class PlacementSystem extends createSystem({}) {
  update(): void {
    if (!hitTestSource) {
      if (reticleRoot) reticleRoot.visible = false;
      return;
    }
    const phase = state.placement.phase;
    if (phase !== "scanning" && phase !== "aiming") {
      if (reticleRoot) reticleRoot.visible = false;
      return;
    }
    const frame = (world.renderer.xr as unknown as { getFrame?: () => XRFrame | null }).getFrame?.();
    const space = world.renderer.xr.getReferenceSpace();
    if (!frame || !space) return;
    const [hit] = frame.getHitTestResults(hitTestSource);
    if (!hit) {
      if (reticleRoot) reticleRoot.visible = false;
      if (phase === "aiming") placement({ type: "lost" });
      return;
    }
    // `XRHitTestResult.getPose`: the one WebXR read the client keeps, because
    // neither framework covers hit testing. The CI input-boundary guard names
    // the frame's own pose read specifically, so this call is not mistaken
    // for it.
    const pose = hit.getPose(space);
    if (!pose) return;
    const p = pose.transform.position;
    const o = pose.transform.orientation;
    placement({ type: "surface", pose: { position: { x: p.x, y: p.y, z: p.z }, yaw: state.transform.yaw } });
    // The reticle's orientation is a purely visual read of this frame's hit
    // pose; it does not go through the (pure, position+yaw only) placement
    // state, so the machine stays engine-free and its tests stay unaffected.
    if (reticleRoot) {
      reticleRoot.position.set(p.x, p.y, p.z);
      reticleRoot.quaternion.set(o.x, o.y, o.z, o.w);
      reticleRoot.visible = showsReticle(state.placement);
    }
  }
}

// --- readout ----------------------------------------------------------------

/** Counts console.error calls and uncaught errors/rejections, for the Status window. */
let consoleErrorCount = 0;

/**
 * Count errors independently of the remote logger, which only patches
 * console when enabled. Installed once, before `initRemoteLogger()`, so the
 * remote logger's own patch (when active) wraps around this one rather than
 * the other way round; either order counts correctly, but this one runs
 * whether or not remote logging is on.
 */
function installConsoleErrorCounter(): void {
  const orig = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    consoleErrorCount += 1;
    orig(...args);
  };
  window.addEventListener("error", () => (consoleErrorCount += 1));
  window.addEventListener("unhandledrejection", () => (consoleErrorCount += 1));
}

let frames = 0;
let lastReadout = 0;
let framesAtLastReadout = 0;
/** Rolling frame rate, recomputed each time `refresh()` fires (every ~500 ms). */
let lastFps = 0;
/** How often a perf line goes to the log: every 5 s in a session, every 30 s on the desk. */
const PERF_LOG_MS_PRESENTING = 5_000;
const PERF_LOG_MS_DESK = 30_000;
const perf = new PerfMonitor();
let lastPerfLogAt = 0;
let lastPerf: PerfSample | null = null;

/** Close the current frame window and add what only the engine knows. */
function perfSample(now: number, presenting: boolean): PerfSample {
  const stats = perf.snapshot();
  const info = world.renderer.info.render;
  const s = scene.stats();
  const hits = hitTester.takeStats();
  const memory = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
  const session = world.renderer.xr.getSession() as unknown as { frameRate?: number } | null;
  return {
    ...stats,
    at: Math.round(now),
    presenting,
    multiview: (world.renderer.xr as unknown as { isMultiview?: boolean }).isMultiview === true,
    sessionHz: session?.frameRate ?? null,
    drawCalls: info.calls,
    triangles: info.triangles,
    subDraws: s.runs,
    instances: s.instances,
    culled: s.culled,
    bodies: s.bodies,
    level,
    hitRays: hits.rayCalls,
    hitMs: hits.rayMs,
    heapMb: memory ? Math.round(memory.usedJSHeapSize / 1048576) : null,
    logRequests: remoteLogRequests(),
  };
}

class ReadoutSystem extends createSystem({}) {
  update(): void {
    frames += 1;
    const now = performance.now();
    picker?.update(now);

    // See RESET_GUARD_MS: keep winning the argument with the locomotion
    // worker's own per-frame write for a short window after a reset. X/Z only
    // — measured on the real atlas, the locomotor settles Y a few millimetres
    // off zero at rest (its own ground-contact resolution, not a race) and
    // forcing it to exactly 0 here only trades that for a step back to 0.009
    // the moment the guard lets go. Y was never the reported defect (the
    // original failure's own y, 1.70, already passed the test's y > 1.2); only
    // x/z genuinely belong at exactly the teleported position.
    if (resetGuardUntil > 0) {
      if (now < resetGuardUntil) {
        const rig = world.camera.parent;
        if (rig && (Math.abs(rig.position.x) > 1e-4 || Math.abs(rig.position.z) > 1e-4)) {
          rig.position.x = 0;
          rig.position.z = 0;
          rig.updateMatrixWorld(true);
        }
      } else {
        resetGuardUntil = 0;
      }
    }

    perf.frame(now, lastRuntimeUpdateMs);
    const presenting = world.renderer.xr.isPresenting === true;
    if (now - lastPerfLogAt >= (presenting ? PERF_LOG_MS_PRESENTING : PERF_LOG_MS_DESK)) {
      lastPerfLogAt = now;
      lastPerf = perfSample(now, presenting);
      console.info("[Atlas] perf", lastPerf);
    }

    if (now - lastReadout < 500) return;
    const elapsedS = (now - lastReadout) / 1000;
    lastFps = elapsedS > 0 ? (frames - framesAtLastReadout) / elapsedS : lastFps;
    framesAtLastReadout = frames;
    lastReadout = now;
    refresh();
  }
}

/**
 * Pointing at the body. Hovering tints a part and names it; pressing selects
 * it, which is the same selection Browse makes, so the card and the isolation
 * behave identically however you got there.
 *
 * Two routes reach the same two functions below: parts through the
 * interaction runtime (`onInteractionEvent`, hands and controllers alike) and
 * the desk mouse through `Picker` (`installPicker()`), both resolving hits
 * with the same `AtlasHitTester`. One hit path, one set of handlers.
 */
let picker: Picker | null = null;

/** The interactable currently hovered (`${bodyId}/${partId}`), split for the UI's convenience. */
let hovered: { interactableId: string; bodyId: string; partId: string } | null = null;

/** The part named by `${bodyId}/${partId}`, or `null` when either half does not resolve. */
function resolvePart(interactableId: string): { bodyId: string; partId: string; part: AtlasPart } | null {
  const slash = interactableId.indexOf("/");
  if (slash < 0) return null;
  const bodyId = interactableId.slice(0, slash);
  const partId = interactableId.slice(slash + 1);
  const part = scene.bodies.get(bodyId)?.atlas.parts.get(partId);
  return part ? { bodyId, partId, part } : null;
}

/** The parts carrying the selection tint: one per body the selection was made on. */
let selectionTint: { bodyId: string; partId: string }[] = [];
let lastHoverLogAt = 0;

/** A part's colour once nothing hovers it: the selection tint if it is selected, else its system's. */
function restoreColour(bodyId: string, partId: string): void {
  const atlas = scene.bodies.get(bodyId)?.atlas;
  if (!atlas) return;
  if (selectionTint.some((t) => t.bodyId === bodyId && t.partId === partId)) atlas.setColour(partId, SELECTED_COLOUR);
  else atlas.resetColour(partId);
}

/** Tint a single-part selection on the bodies it applies to; a wider group (a system, a concept) carries no tint. */
function tintSelection(group: Group, bodyId: string | null): void {
  clearSelectionTint();
  if (group.parts.length !== 1) return;
  const partId = group.parts[0]!;
  for (const id of bodyId ? [bodyId] : [...targets()]) {
    selectionTint.push({ bodyId: id, partId });
    // the hover tint stays on top until the pointer moves on
    if (hovered?.bodyId === id && hovered.partId === partId) continue;
    scene.bodies.get(id)?.atlas.setColour(partId, SELECTED_COLOUR);
  }
}

function clearSelectionTint(): void {
  const previous = selectionTint;
  selectionTint = [];
  for (const t of previous) {
    if (hovered?.bodyId === t.bodyId && hovered.partId === t.partId) continue;
    scene.bodies.get(t.bodyId)?.atlas.resetColour(t.partId);
  }
}

/** Tint the newly hovered part, put the previous one back, and name it. */
function setHoveredInteractable(id: string | null): void {
  if (id === (hovered?.interactableId ?? null)) return;
  if (hovered) restoreColour(hovered.bodyId, hovered.partId);
  const resolved = id ? resolvePart(id) : null;
  hovered = resolved ? { interactableId: id as string, bodyId: resolved.bodyId, partId: resolved.partId } : null;
  if (!resolved) return;
  scene.bodies.get(resolved.bodyId)?.atlas.setColour(resolved.partId, HOVER_COLOUR);
  // name the body too while several are up, so it is clear which one a press
  // is about to act on before the press happens
  say(state.comparing ? `${resolved.part.record.name} on ${labelOf(resolved.bodyId)}` : resolved.part.record.name);
  // in a session the desk cannot see the tint, so the hover is logged too, at most twice a second
  const now = performance.now();
  if (world.renderer.xr.isPresenting && now - lastHoverLogAt > 500) {
    lastHoverLogAt = now;
    console.info("[Atlas] hover", resolved.part.record.name, state.comparing ? `on ${labelOf(resolved.bodyId)}` : "");
  }
}

/** Select the part named by an interactable id — the same selection Browse makes. */
function selectInteractable(id: string): void {
  const resolved = resolvePart(id);
  if (!resolved) return;
  const groups = groupsFor(catalog, resolved.part.record.id);
  // the part itself is the most specific answer, and the one a person pointing means
  const group = groups[groups.length - 1];
  if (!group) return;
  // a press selects; it never hides the rest of the body (see `select`)
  select(group, resolved.bodyId, { isolate: false });
}

/** Commit the current hover as a selection. Returns true when something was picked. */
function commitHover(): boolean {
  if (!hovered) return false;
  selectInteractable(hovered.interactableId);
  return true;
}

/**
 * Ground for the locomotion engine to stand the player on.
 *
 * The grid is a `GridHelper`, which is lines rather than a surface, so there is
 * nothing to walk on without this. The plane is invisible: the grid remains the
 * floor a person sees, and this is only what the ground detector traces
 * against. `LocomotionEnvironment` is the component that marks it walkable.
 */
function installFloor(): void {
  const floor = new Mesh(new PlaneGeometry(40, 40), new MeshBasicMaterial({ visible: false }));
  floor.rotation.x = -Math.PI / 2;
  floor.name = "locomotion-floor";
  const entity = world.createTransformEntity(floor);
  entity.addComponent(LocomotionEnvironment, { type: "static" });
}

/**
 * Looking around on a desk, which IWSDK deliberately leaves to the app: its
 * browser bindings move the player but never rotate the camera.
 *
 * Right-drag looks, which is what the Reality Collective playground binds and
 * is unambiguous. Left-drag looks too, because it is what people try first, and
 * a left press that does not travel `DRAG_SLOP` is still a pick, so the one
 * button serves both without a mode.
 *
 * Not pointer lock, for the reason their `DesktopControls` gives: it takes the
 * cursor away from the controls, and this app's controls are a DOM panel and
 * clickable parts.
 */
const DRAG_SLOP = 4;
const LOOK_SPEED = 0.0022;
const MAX_PITCH = Math.PI / 2 - 0.01;

/**
 * Metres per second on the desk. IWSDK's default is a run, which overshoots a
 * body that is 1.8 m tall and a stride away; this is a walk, which is the pace
 * the thing is meant to be read at.
 */
const WALK_SPEED = 1.6;

/**
 * Keep typing out of the locomotion bindings.
 *
 * IWSDK's keyboard listener is on `window` with no guard for form fields, and
 * WASD are movement keys, so searching for "head" would walk the player
 * backwards and strafe them left. Window is the last stop on the bubble path,
 * so stopping the event at the document, after the field has had it, keeps the
 * text and drops the movement.
 */
function installKeyboardGuard(): void {
  const isTyping = (target: EventTarget | null): boolean => {
    const el = target as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
  };
  for (const type of ["keydown", "keyup"] as const) {
    document.addEventListener(type, (event) => {
      if (isTyping(event.target)) event.stopPropagation();
    });
  }
}

function installLook(): void {
  const canvas = world.renderer.domElement;
  let down: { x: number; y: number } | null = null;
  let dragging = false;
  let yaw = 0;
  let pitch = 0;

  // right-drag is a look, never a context menu
  canvas.addEventListener("contextmenu", (event) => event.preventDefault());

  canvas.addEventListener("pointerdown", (event) => {
    // the renderer owns the camera inside a session, so leave it alone there
    if ((event.button !== 0 && event.button !== 2) || world.renderer.xr.isPresenting) return;
    down = { x: event.clientX, y: event.clientY };
    // a right press is a look from the outset; a left press has to travel first
    dragging = event.button === 2;
    if (dragging) {
      canvas.setPointerCapture(event.pointerId);
      picker?.clearPointer();
    }
    // Take the angles from wherever the camera is now, so framing the body or
    // the comparison row does not leave this holding a stale heading.
    const euler = world.camera.rotation.clone();
    euler.reorder("YXZ");
    yaw = euler.y;
    pitch = euler.x;
  });

  canvas.addEventListener("pointermove", (event) => {
    if (!down) return;
    if (!dragging && Math.hypot(event.clientX - down.x, event.clientY - down.y) < DRAG_SLOP) return;
    if (!dragging) {
      dragging = true;
      canvas.setPointerCapture(event.pointerId);
      picker?.clearPointer();
    }
    yaw -= event.movementX * LOOK_SPEED;
    pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pitch - event.movementY * LOOK_SPEED));
    world.camera.rotation.set(pitch, yaw, 0, "YXZ");
    world.camera.updateMatrixWorld(true);
  });

  const end = (event: PointerEvent): void => {
    if (dragging && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    down = null;
    // a drag is a look, not a pick, so the click handler must not also select
    if (dragging) setTimeout(() => (dragging = false), 0);
  };
  canvas.addEventListener("pointerup", end);
  canvas.addEventListener("pointercancel", end);
  canvas.addEventListener("click", (event) => {
    if (dragging) event.stopImmediatePropagation();
  }, true);
}

/**
 * Pace on the desk: a walk, or a run while Shift is held, which is the pair the
 * Reality Collective playground binds. Crossing an eight metre room at
 * inspection speed is tedious, and inspecting at crossing speed overshoots a
 * body two strides wide.
 *
 * The feature flag carries no speed of its own, so the system is configured
 * after `World.create` has registered it.
 */
const SPRINT_MULTIPLIER = 3;

/**
 * Where the camera sits inside the rig when the person is simply standing.
 * Captured at boot rather than assumed, because the height is the host's.
 */
let cameraHome: Vector3 | null = null;

/**
 * How long `resetView()` keeps re-asserting the origin after it runs.
 *
 * `LocomotionSystem.update()` copies its `Locomotor`'s position onto the rig
 * unconditionally, every frame, and worker mode teleports asynchronously: a
 * `PositionUpdate` message already in flight from before the teleport was
 * requested can still land afterwards and briefly pull the rig back towards
 * wherever it was before, before the worker catches up with the teleport.
 * `setPlayerPosition` cannot see that message coming, so the fix is to keep
 * winning the argument for a short window rather than trust a single write.
 */
const RESET_GUARD_MS = 600;
let resetGuardUntil = 0;

/**
 * Walk back to where you started.
 *
 * Once the scene can be walked it can be walked away from, and the body is a
 * small thing in a large room. This returns the player to the origin and the
 * camera to standing, level and facing forwards, which is also what undoes a
 * framing call made by a capture or a test.
 */
function resetView(): void {
  const locomotion = world.getSystem(LocomotionSystem);
  const origin = new Vector3(0, 0, 0);
  if (locomotion) locomotion.setPlayerPosition(origin);
  const rig = world.camera.parent;
  if (rig) {
    rig.position.set(0, 0, 0);
    rig.rotation.set(0, 0, 0);
    rig.updateMatrixWorld(true);
  }
  if (cameraHome) world.camera.position.copy(cameraHome);
  world.camera.rotation.set(0, 0, 0, "YXZ");
  world.camera.updateMatrixWorld(true);
  // See RESET_GUARD_MS: the locomotion worker can still overwrite this once
  // or twice before it catches up, so ReadoutSystem keeps re-asserting it.
  resetGuardUntil = performance.now() + RESET_GUARD_MS;
  say("back to where you started");
}

function installWalking(): void {
  const locomotion = world.getSystem(LocomotionSystem);
  if (!locomotion) {
    console.warn("[Atlas] locomotion is not registered, so the desk build cannot walk");
    return;
  }
  const setPace = (sprinting: boolean): void => {
    locomotion.config.slidingSpeed.value = sprinting ? WALK_SPEED * SPRINT_MULTIPLIER : WALK_SPEED;
  };
  setPace(false);
  // Listened for on the window, so the guard at the document has already
  // dropped the event when the person is typing rather than walking.
  window.addEventListener("keydown", (event) => {
    if (event.key === "Shift") setPace(true);
  });
  window.addEventListener("keyup", (event) => {
    if (event.key === "Shift") setPace(false);
  });
  // a window that loses focus mid-stride never delivers the keyup, and a sprint
  // nobody asked for is worse than a walk nobody noticed
  window.addEventListener("blur", () => setPace(false));
}

/**
 * The desk mouse: a thin driver over `AtlasHitTester`, wired to the same
 * `setHoveredInteractable`/`commitHover` that hands and controllers drive
 * through `onInteractionEvent`. The frameworks have no 2D pointer source, so
 * this is the one input path that stays hand-written.
 */
function installPicker(): void {
  picker = new Picker(world, hitTester, setHoveredInteractable);

  const canvas = world.renderer.domElement;
  canvas.addEventListener("pointermove", (event) => {
    const rect = canvas.getBoundingClientRect();
    picker?.setPointer(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
  });
  canvas.addEventListener("pointerleave", () => picker?.clearPointer());
  canvas.addEventListener("click", () => {
    // Hover is throttled to 20 Hz (`Picker.update`'s `HOVER_INTERVAL_MS`), so
    // committing alone can select whatever was last hovered rather than what
    // is under the pointer right now; `pickNow()` resolves fresh first.
    picker?.pickNow();
    commitHover();
  });
}

function refresh(): void {
  // the spatial windows read the same state as the desk panel, so anything that
  // changes it repaints both
  windows?.paint();
  renderArButton();
  const s = scene.stats();
  const info = world.renderer.info.render;
  const lines = [
    `${s.bodies} bod${s.bodies === 1 ? "y" : "ies"} · ${s.instances} parts`,
    `sub-draws ${s.runs} · calls ${info.calls}`,
    `triangles ${info.triangles.toLocaleString()}`,
    `visible ${trianglesVisible(catalog, resolveVisibility(catalog, visibilityOf(focused()))).toLocaleString()} on ${state.comparing ? labelOf(focused()) : "the body"}`,
    ...(state.comparing ? [`acting on: ${labelOf(state.active)}`] : []),
    state.selected ? `selected: ${state.selected.name}` : "nothing selected",
    `placement: ${state.placement.phase}${showsReticle(state.placement) ? " (aiming)" : ""}`,
  ];
  readoutEl.textContent = lines.join("\n");
}

// --- boot -------------------------------------------------------------------

let entry: EntryOverlay | null = null;
let windows: AtlasWindows | null = null;

/**
 * What the spatial windows are allowed to do. The same operations the DOM
 * panel performs, named once, so the two interfaces cannot drift apart: adding
 * a control to one and forgetting the other shows up here as an unused method
 * rather than as a headset-only bug found weeks later.
 */
const uiPort: AtlasUiPort = {
  catalog: () => catalog,
  visibility: () => visibilityOf(focused()),
  selected: () => state.selected,
  comparing: () => state.comparing,
  status: () => statusEl.textContent ?? "",
  bodyOptions: () => bodyOptions(),
  activeBody: () => (state.comparing ? state.active : SOLO),
  setActiveBody: (id) => setActive(id),

  applyPreset: (preset) => {
    for (const id of targets()) setVisibility(id, applyPreset(catalog, visibilityOf(id), preset));
    renderSystems();
    refresh();
  },
  toggleSystem: (systemId) => {
    for (const id of targets()) setVisibility(id, toggleSystem(visibilityOf(id), systemId));
    renderSystems();
    refresh();
  },
  selectGroup: (group) => select(group),
  hideSelected: () => {
    const group = state.selected;
    if (!group) return;
    for (const id of targets()) {
      let next = visibilityOf(id);
      for (const part of group.parts) next = hidePart(next, part);
      setVisibility(id, next);
    }
    say(`hid ${group.name}`);
    refresh();
  },
  showEverything: () => showEverything(),
  nudgeScale: (factor) => nudge((t) => scaleAbout(t, centreOfBody(t), factor)),
  nudgeYaw: (radians) => nudge((t) => yawAbout(t, centreOfBody(t), radians)),
  setComparing: (on) => setComparing(on),
  reset: () => applyTransformToAnchor(framedInFront({ x: 0, y: 1.6, z: 0 }, { x: 0, y: 0, z: -1 }, 1.6, 1)),

  cardInfo: () => cardInfo(),
  arSupported: () => arSupported,
  inAr: () => inAr(),
  toggleAr: () => void toggleAr(),

  diagnostics: (): StatusReport => {
    const session = world.renderer.xr.getSession();
    const presenting = world.renderer.xr.isPresenting === true;
    const ar = presenting && inAr();
    const info = world.renderer.info.render;
    return {
      bundle: manifest.id,
      level,
      mode: !presenting ? "2D" : ar ? "AR" : "VR",
      blendMode: session?.environmentBlendMode ?? "none",
      multiview: (world.renderer.xr as unknown as { isMultiview?: boolean }).isMultiview === true,
      layers: presenting ? layersEnabled : null,
      arSupported,
      hitTest: !ar ? "n/a" : hitTestSource ? "available" : "unavailable",
      fps: Math.round(lastFps),
      drawCalls: info.calls,
      triangles: info.triangles,
      perfLine: perfLine(lastPerf),
      placementPhase: state.placement.phase,
      consoleErrors: consoleErrorCount,
      logSessionId: remoteLogSessionId,
      logStreaming: isRemoteLogEnabled(),
    };
  },
  about: (): AboutReport => ({
    bundleId: manifest.id,
    run: manifest.provenance?.run ?? "unknown",
    threeRevision: REVISION,
    iwsdkCore: "0.5.3",
    multiviewFix: (globalThis as unknown as { __superThreeMultiviewFix?: string }).__superThreeMultiviewFix === "applied",
    controlsSummary: "Controllers: trigger to select, hold the body with the grip to move it, both grips to scale and turn it. Hands: pinch to select and to walk through Browse, hold the body with a pinch to move it, both hands pinching to scale and turn it.",
  }),
};

async function boot(): Promise<void> {
  // Installed before anything else runs, so a boot failure is still counted
  // and, if enabled, still reaches the desk.
  installConsoleErrorCounter();
  initRemoteLogger();
  // Cached rather than probed per press: "AR supported" on the Status window
  // and the act-ar/enter-ar buttons both read this same one-time result.
  void navigator.xr
    ?.isSessionSupported("immersive-ar")
    .catch(() => false)
    .then((ok) => {
      arSupported = ok;
      console.info("[Atlas] AR probe", { supported: ok });
      renderArButton();
    });

  world = await World.create(el<HTMLDivElement>("scene-container"), {
    xr: { sessionMode: SessionMode.ImmersiveVR, offer: "always", features: { handTracking: true, layers: true } },
    features: {
      // Walking is how you read a body at life size, and it is the one part of
      // the headset experience a desk can honestly reproduce. IWSDK's own
      // browser bindings supply WASD, the arrow keys and a gamepad stick, and
      // they move `world.player` along whatever direction the camera faces.
      // Turning the camera is left to the app, which `installLook()` does.
      locomotion: { browserControls: { keyboard: true, gamepad: true }, enableJumping: false },
      // Registers IWSDK's `GrabSystem`: a hand pinch is routed to the grab
      // sub-pointer's `squeeze` channel the same way a controller's squeeze
      // button is, so `TwoHandsGrabbable` (`installBodyAnchor()`) works with
      // either without a separate hand-tracking path.
      grabbing: { useHandPinchForGrab: true },
      physics: false,
      sceneUnderstanding: false,
      environmentRaycast: false,
      spatialUI: true,
    },
  });

  // The service runtime owns the session. The entry gate then talks to a facet
  // that resolves a result instead of throwing, which is what lets a dismissed
  // permission prompt come back as a button the person can press again.
  // the host decides standing height, so record it before anything moves it
  cameraHome = world.camera.position.clone();

  runtime = startServiceRuntime(world, () => createServiceProfile("anatomy-atlas", []));
  if (showEntry && runtime.adapter.session) {
    entry = installEntryOverlay(runtime.adapter.session, {
      title: "Anatomy Atlas",
      standfirst: "A human body at life size, in your room. Peel it back system by system, lift out a single structure, or stand three bodies side by side and compare them.",
      action: "Explore anatomy",
      screenAction: "Explore on this screen",
      onDismiss: () => say(`Exploring on screen. ${DESK_CONTROLS}.`),
      image: "entry/atlas-row.jpg",
      credit: "Anatomy from BodyParts3D, © The Database Center for Life Science, CC BY 4.0.",
    });
  }
  if (!showPanel) el("controls").style.display = el("readout").style.display = "none";

  world.scene.background = new Color("#171d24");
  world.scene.add(new HemisphereLight(0xffffff, 0x40506a, 0.95));
  const key = new DirectionalLight(0xffffff, 1.5);
  key.position.set(1.4, 2.4, 1.1);
  world.scene.add(key, new AmbientLight(0xffffff, 0.18));
  // the floor grid orients a person in VR, but it crosses the frame as faint
  // streaks in a captured still, so `grid=off` drops it for the entry image
  if (params.get("grid") !== "off") {
    gridHelper = new GridHelper(8, 16, 0x5c6c7c, 0x333e49);
    world.scene.add(gridHelper);
  }
  installFloor();
  installReticle();

  say(`loading ${bundleBase}…`);
  const ports = browserPorts();
  manifest = await loadManifest(bundleBase, ports);
  catalog = buildCatalog(manifest, level);
  el("bundle").textContent = `${manifest.name} · ${catalog.parts.length} parts · ${catalog.systems.length} systems · ${level}`;
  console.info("[Atlas] boot", {
    bundle: manifest.id,
    level,
    threeRevision: REVISION,
    multiviewFix: (globalThis as unknown as { __superThreeMultiviewFix?: string }).__superThreeMultiviewFix ?? "not-multiview",
    ua: navigator.userAgent,
  });

  scene = new AtlasScene(manifest, catalog, ports, bundleBase, { level, systems: catalog.systems.map((s) => s.id) });
  await scene.load((done, total) => {
    say(`chunks ${done}/${total}`);
    entry?.setProgress(`Loading the atlas… ${done} of ${total}`);
  });
  world.scene.add(scene.root);
  installBodyAnchor();
  installInteractions();
  installPointerVisuals();

  // start where a standing person can see the whole body; `layoutSolo()`
  // applies it to the anchor once the solo body (and so the anchor's child)
  // exists.
  state.transform = framedInFront({ x: 0, y: 1.6, z: 0 }, { x: 0, y: 0, z: -1 }, 1.6, 1);
  layoutSolo();

  renderSystems();
  renderPresets();
  renderBodyTargets();
  renderCard();
  renderArButton();
  el("desk-controls").textContent = `${DESK_CONTROLS.charAt(0).toUpperCase()}${DESK_CONTROLS.slice(1)}.`;
  wirePanel();
  wireSession();
  world.registerSystem(ManipulationSystem);
  world.registerSystem(PlacementSystem);
  world.registerSystem(ReadoutSystem);

  // The in-headset interface. The DOM panel above is the desk build; once the
  // headset is on it cannot be reached, so the same controls are spawned as UI
  // Extensions windows driven by the same port.
  registerUIExtensions(world);
  windows = new AtlasWindows(world, uiPort);
  windows.create();
  // Spawned now so their binding is proven on the desk (the smoke asserts
  // five), shown only inside a session: on a desk the panel has everything
  // the windows would repeat, and they would sit in front of the camera.
  windows.setVisible(false);
  installPicker();
  installLook();
  installKeyboardGuard();
  installWalking();

  // the gate's button stays disabled until here, so pressing it enters at once
  // rather than starting a wait the person cannot see the end of
  entry?.ready();
  say("ready — search, toggle systems, or enter VR");

  (window as unknown as { __atlasCamera?: unknown }).__atlasCamera = world.camera;
  (window as unknown as { __atlasClient?: unknown }).__atlasClient = {
    ready: true,
    bundle: manifest.id,
    parts: catalog.parts.length,
    systems: catalog.systems.length,
    concepts: catalog.concepts.length,
    stats: () => scene.stats(),
    /**
     * Per-body draw and layer state. `stats()` sums across the bodies, which
     * cannot answer "does each copy carry its own layers?"; the desktop
     * usability test asks exactly that, so each body reports its own.
     */
    bodies: () =>
      [...scene.bodies.values()].map((body) => {
        const s = body.atlas.stats();
        return {
          id: body.id,
          instances: s.instances,
          triangles: s.triangles,
          runs: s.runs,
          culled: s.culled,
          systemsOn: [...body.visibility.systems].sort(),
          isolated: body.visibility.isolated ? [...body.visibility.isolated].sort() : null,
          hidden: [...body.visibility.hidden].sort(),
          transform: { position: { ...body.transform.position }, scale: body.transform.scale, yaw: body.transform.yaw },
        };
      }),
    select: (query: string) => {
      const [hit] = search(catalog, query, { limit: 1 });
      if (hit) select(hit.group);
      return hit?.group ?? null;
    },
    compare: (on: boolean) => setComparing(on),
    frameComparison: () => frameComparison(),
    frameBody: () => frameBody(),
    windows: () => windows?.boundCount() ?? 0,
    hovered: () => hovered?.partId ?? null,
    pointAt: (x: number, y: number) => {
      picker?.setPointer(x, y);
      picker?.pickNow();
      return hovered?.partId ?? null;
    },
    clickPointer: () => commitHover(),
    pickDebug: () => ({ ...picker?.debug(), camera: [world.camera.position.x, world.camera.position.y, world.camera.position.z].map((n) => Number(n.toFixed(2))), bounds: catalog.bounds }),
    showEverything: () => showEverything(),
    state: () => ({ comparing: state.comparing, active: state.active, selected: state.selected?.id ?? null, placement: state.placement.phase, presenting: world.renderer.xr.isPresenting === true }),
    /** Aim the controls at one body, or at all of them with null. */
    setActive: (id: string | null) => setActive(id),
    hoveredBody: () => hovered?.bodyId ?? null,
    resetView: () => resetView(),
    diagnostics: () => uiPort.diagnostics(),
    about: () => uiPort.about(),
    toggleAr: () => void toggleAr(),
    /** Parts registered on the interaction runtime — parts × bodies, or the documented fallback. */
    interactables: () => interactions.runtime.getInteractableIds().length,
    /** Whether the body anchor entity carries `TwoHandsGrabbable`. */
    anchorGrabbable: () => anchorEntity?.hasComponent(TwoHandsGrabbable) ?? false,
    /** Last measured cost of `interactions.runtime.update()`, in milliseconds. */
    runtimeUpdateMs: () => lastRuntimeUpdateMs,
    /** Raycast acceleration trees built so far, out of the systems that can have one. */
    boundsTrees: () => scene.boundsTreeStatus(),
    /** Build or drop every body's trees now: the tests compare the two raycast paths. */
    boundsTreesBuild: () => scene.bodies.forEach((b) => b.atlas.buildBoundsTrees()),
    boundsTreesDispose: () => scene.bodies.forEach((b) => b.atlas.disposeBoundsTrees()),
    /** Ray queries answered since the last call and their cost, from the hit tester. */
    hitStats: () => hitTester.takeStats(),
    /** The last perf sample, or a fresh one when none has been logged yet. */
    perf: () => lastPerf ?? perfSample(performance.now(), world.renderer.xr.isPresenting === true),
    /**
     * Log a perf sample now. The cadence is 5 s presenting and 30 s on a desk,
     * which is longer than an end-to-end test should wait, so the test asks
     * for one (`test/e2e-console.mjs`); a tester can call it from the console
     * of a tethered headset to mark a moment.
     */
    logPerf: () => {
      lastPerf = perfSample(performance.now(), world.renderer.xr.isPresenting === true);
      console.info("[Atlas] perf", lastPerf);
      return lastPerf;
    },
  };
}

/**
 * Put the camera at a world position, whatever the player rig is doing.
 *
 * The camera hangs off `world.player`, so its own `position` is relative to a
 * rig that locomotion moves. Framing works in world space, so the target has to
 * be converted or the shot lands wherever the person happened to walk to.
 * `lookAt` needs no such treatment: three resolves it against the world matrix.
 */
function placeCamera(camera: typeof world.camera, x: number, y: number, z: number): void {
  const target = new Vector3(x, y, z);
  const rig = camera.parent;
  if (rig) {
    rig.updateMatrixWorld(true);
    rig.worldToLocal(target);
  }
  camera.position.copy(target);
}

/**
 * Put the 2D camera where the whole comparison row is in shot, front on. Used
 * by `scripts/capture-entry-image.mjs` to make the entry page's background from
 * the client itself rather than from an illustration.
 */
/**
 * Point the 2D camera at the solo body, framed head to foot. Used by the desk
 * build's *look at the body* and by the smoke test, which needs the body in a
 * known place before it can point at it.
 */
function frameBody(): void {
  const [min, max] = catalog.bounds;
  const { position, scale, yaw } = state.transform;
  // Frame the bundle's actual bounds. Assuming the geometry starts at the floor
  // and rises works for the atlas, whose feet sit at y = 0, and misses entirely
  // for a bundle whose origin is somewhere else.
  const half = [0, 1, 2].map((k) => (((max[k] ?? 0) - (min[k] ?? 0)) / 2) * scale);
  const local = { x: (((min[0] ?? 0) + (max[0] ?? 0)) / 2) * scale, y: (((min[1] ?? 0) + (max[1] ?? 0)) / 2) * scale, z: (((min[2] ?? 0) + (max[2] ?? 0)) / 2) * scale };
  const turned = rotateY(local, yaw);
  const centre = { x: position.x + turned.x, y: position.y + turned.y, z: position.z + turned.z };

  const camera = world.camera;
  const vHalf = (camera.fov * Math.PI) / 360;
  const hHalf = Math.atan(Math.tan(vHalf) * camera.aspect);
  const distance = Math.max((half[1] ?? 1) / Math.tan(vHalf), (half[0] ?? 1) / Math.tan(hHalf)) * 1.2 + (half[2] ?? 0);
  placeCamera(camera, centre.x, centre.y, centre.z + distance);
  camera.lookAt(centre.x, centre.y, centre.z);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
}

function frameComparison(bias = 0.25): void {
  const slots = compareRow(catalog, DEFAULT_COMPARISON, { centre: { x: 0, y: 0, z: -2.2 }, scale: 0.9 });
  const [, max] = catalog.bounds;
  const height = (max[1] ?? 1.8) * 0.9;
  const halfWidth = Math.abs(slots[slots.length - 1]!.transform.position.x) + bodyWidth(catalog) * 0.9 * 0.5;
  const camera = world.camera;

  // Offsetting the camera sideways puts the row right of centre and leaves the
  // left of the frame for the title and the button, which is where the entry
  // page's scrim is darkest.
  const shift = halfWidth * 2 * bias;
  const vHalf = (camera.fov * Math.PI) / 360;
  const hHalf = Math.atan(Math.tan(vHalf) * camera.aspect);
  // hold the widened row horizontally and the whole body vertically
  const forWidth = (halfWidth + shift) / Math.tan(hHalf);
  const forHeight = (height / 2) / Math.tan(vHalf);
  // The margin keeps the whole row inside a 4:3 crop of a 16:9 capture: the
  // entry page covers the viewport with the image and anchors it at 62 %,
  // where the row's centre lands with this bias, so a narrower window loses
  // the image's edges and never a body.
  const distance = Math.max(forWidth, forHeight) * 1.35;

  // Straight down the row's axis so the bodies stay square to the camera, but
  // tilted down a little: level with the chest the floor grid is edge on and
  // reads as streaks across the frame, where a slight look-down grounds it.
  placeCamera(camera, -shift, height * 0.66, -2.2 + distance);
  camera.lookAt(-shift, height * 0.44, -2.2);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
}

/**
 * Session lifecycle logging and the placement machine's exit. The confirm
 * and adjust taps used to come from the WebXR `select` event fired straight
 * off the session; they come from `onInteractionSample` now
 * (`installInteractions()`), which works the same whether or not a session
 * is live, so this function no longer listens for input at all.
 */
function wireSession(): void {
  const xr = world.renderer.xr;
  xr.addEventListener("sessionstart", () => {
    const session = xr.getSession();
    if (!session) return;
    const ar = session.environmentBlendMode !== "opaque";
    // The spatial windows are the headset's interface; the desk has the panel.
    windows?.setVisible(true);
    // the frames before the session are not its own
    perf.reset(performance.now());
    lastPerfLogAt = performance.now();
    const multiview = (xr as unknown as { isMultiview?: boolean }).isMultiview === true;
    layersEnabled = session.enabledFeatures?.includes("layers") ?? false;
    console.info("[Atlas] session start", {
      mode: ar ? "AR" : "VR",
      blendMode: session.environmentBlendMode,
      isMultiview: multiview,
      enabledFeatures: session.enabledFeatures ? [...session.enabledFeatures] : [],
    });
    say(`session started (${ar ? "AR" : "VR"})`);
    // The grid orients a person in the studio; in AR the real floor does that
    // job, and EnvironmentSystem already clears the sky and background alpha.
    if (gridHelper) gridHelper.visible = !ar;
    renderArButton();
    if (ar) void startHitTesting(session);
  });
  xr.addEventListener("sessionend", () => {
    windows?.setVisible(false);
    lastPerf = perfSample(performance.now(), true);
    console.info("[Atlas] session end", lastPerf);
    hitTestSource = null;
    layersEnabled = null;
    if (gridHelper) gridHelper.visible = true;
    if (reticleRoot) reticleRoot.visible = false;
    // the end of the VR session that a "Place in my room" swap asked for is
    // not an exit from placement; the swap owns the machine until AR answers
    if (!arSwapInProgress) placement({ type: "exit" });
    renderArButton();
  });
}

function wirePanel(): void {
  const searchBox = el<HTMLInputElement>("search");
  searchBox.addEventListener("input", () => renderResults(search(catalog, searchBox.value, { limit: 12 })));
  el("clear-isolation").addEventListener("click", showEverything);
  el("compare-on").addEventListener("click", () => setComparing(true));
  el("compare-off").addEventListener("click", () => setComparing(false));
  el("enter-vr").addEventListener("click", () => void enterVr());
  el("reset-view").addEventListener("click", () => resetView());
  el("enter-ar").addEventListener("click", () => void toggleAr());
  el("reset").addEventListener("click", () => uiPort.reset());
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-scale]")) {
    button.addEventListener("click", () => nudge((t) => scaleAbout(t, centreOfBody(t), Number(button.dataset.scale))));
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-yaw]")) {
    button.addEventListener("click", () => nudge((t) => yawAbout(t, centreOfBody(t), Number(button.dataset.yaw))));
  }
}

/** Turn and scale about the body's middle rather than the floor between its feet. */
function centreOfBody(t: BodyTransform): Vec3 {
  const [min, max] = catalog.bounds;
  const height = ((max[1] ?? 1.8) - (min[1] ?? 0)) / 2;
  return { x: t.position.x, y: t.position.y + height * t.scale, z: t.position.z };
}

void boot().catch((error: unknown) => {
  const text = error instanceof Error ? error.message : String(error);
  say(`failed: ${text}`);
  entry?.fail(`The atlas could not be loaded: ${text}`);
  console.error(error);
});

export {};
